import { useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/format-currency'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { Plan } from '@/components/Configuracao/plansTabTypes'
import { groupPlansByCategory, PLAN_CATEGORY_COLORS } from '@/components/Configuracao/plansTabUtils'
import { mapApiPlanToOption } from '@/components/Deals/DealDetailPanel/DealProposalTab/PlanSelector'
import type { PlanOption } from '@/components/Deals/DealDetailPanel/DealProposalTab/types'

export type CadastroPlanoOption = {
  id: string
  nome: string
  descricao: string
  valorMensal: number
}

type Props = {
  planId: string
  onSelect: (plan: CadastroPlanoOption) => void
}

function toGroupPlan(plan: PlanOption): Plan {
  return {
    id: plan.id,
    name: plan.nome,
    category: plan.category ?? '',
    description: plan.descricao,
    type: 'RECORRENTE',
    baseValue: String(plan.valorMensal),
    config: {},
  }
}

export function ClientesAgradaPlanoSelect({ planId, onSelect }: Props) {
  const [plans, setPlans] = useState<PlanOption[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    api
      .get('/plans', { params: { limit: 100 } })
      .then(({ data }) => {
        const raw = data.data ?? data ?? []
        if (!cancelled) setPlans(raw.map((row: unknown) => mapApiPlanToOption(row)))
      })
      .catch((err: unknown) => {
        if (!cancelled) toast.error(getFriendlyError(err, 'Erro ao carregar planos'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    function onPointer(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [])

  const selected = plans.find((plan) => plan.id === planId)
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return plans
    return plans.filter(
      (plan) =>
        plan.nome.toLowerCase().includes(query) ||
        plan.descricao.toLowerCase().includes(query) ||
        (plan.category ?? '').toLowerCase().includes(query),
    )
  }, [plans, search])

  const groups = useMemo(() => {
    const byId = new Map(filtered.map((plan) => [plan.id, plan]))
    return Object.entries(groupPlansByCategory(filtered.map(toGroupPlan)))
      .map(([categoryKey, items]) => ({
        categoryKey,
        plans: items.map((item) => byId.get(item.id)).filter((plan): plan is PlanOption => Boolean(plan)),
      }))
      .filter((group) => group.plans.length > 0)
  }, [filtered])

  return (
    <div className='relative' ref={rootRef}>
      <Search size={14} className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted' />
      <input
        className='w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] py-2 pl-9 pr-3 text-sm text-text-primary'
        value={open ? search : selected?.nome ?? ''}
        placeholder={loading ? 'Carregando planos...' : 'Buscar planos...'}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setSearch(event.target.value)
          setOpen(true)
        }}
      />
      {open && !loading && (
        <div className='absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-lg border border-[var(--glass-border)] bg-bg-card shadow-xl'>
          {groups.length === 0 ? (
            <p className='px-3 py-3 text-sm text-text-muted'>Nenhum plano encontrado</p>
          ) : (
            groups.map((group) => {
              const colors = PLAN_CATEGORY_COLORS[group.categoryKey] || PLAN_CATEGORY_COLORS.OUTROS
              return (
                <div key={group.categoryKey}>
                  <div
                    className='sticky top-0 flex items-center gap-2 border-b border-l-4 border-[var(--glass-border)] bg-bg-card px-3 py-2'
                    style={{ borderLeftColor: colors.border }}
                  >
                    <span className='text-[10px] font-bold uppercase tracking-wider' style={{ color: colors.text }}>
                      {group.categoryKey}
                    </span>
                    <span className='text-[10px] text-text-muted'>{group.plans.length}</span>
                  </div>
                  {group.plans.map((plan) => (
                    <button
                      key={plan.id}
                      type='button'
                      className={cn(
                        'flex w-full items-center justify-between gap-3 border-b border-[var(--glass-border)] px-3 py-2.5 text-left last:border-0 hover:bg-white/[0.06]',
                        plan.id === planId && 'bg-accent/10',
                      )}
                      onClick={() => {
                        onSelect({
                          id: plan.id,
                          nome: plan.nome,
                          descricao: plan.descricao,
                          valorMensal: plan.valorMensal,
                        })
                        setSearch('')
                        setOpen(false)
                      }}
                    >
                      <span className='min-w-0'>
                        <span className='block text-sm text-text-primary'>{plan.nome}</span>
                        {plan.descricao && (
                          <span className='line-clamp-2 block text-xs text-text-muted'>{plan.descricao}</span>
                        )}
                      </span>
                      <span className='shrink-0 text-xs font-medium text-accent'>{formatCurrency(plan.valorMensal)}</span>
                    </button>
                  ))}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
